/**
 * runCaptionEdit — pure, dependency-injected control flow for the caption
 * rewrite action (Axis 1: original selection, Axis 3: save + Undo).
 *
 * Extracted from the `runCaptionEdit` closure in PostBody.tsx so the three
 * axes required by the post-edit-flow spec can be unit-tested without
 * mounting a React component.
 *
 * The component wires in its captured state and bound helpers as `deps` and
 * delegates to this function. Behaviour is byte-for-byte equivalent to the
 * original closure.
 */

import { toRewritePlatform } from './caption-writing-tools';

import { friendlyMessageWithRetry } from '@/lib/errors/friendly-errors';
import type { RewritePlatform } from '@/services/media/caption-rewrite';

export interface RunCaptionEditDeps {
  /** Per-tab resolved caption from the server (master or platform_captions[tab]). */
  activeCaptionSource?: string | null;
  /** The user's in-progress textarea value. */
  captionDraft?: string | null;
  /** True when captionDraft differs from activeCaptionSource. */
  activeCaptionIsDirty: boolean;
  /** The currently selected platform tab, e.g. "instagram". */
  activePlatform: string | null;
  /** The rewrite instruction (verb chip, tone, or freeform prompt). */
  instruction: string;
  /** Supabase access token — required by the rewrite service. */
  accessToken?: string;
  /** Injected rewrite service — makes the fn testable without network. */
  rewriteCaption: (args: {
    original: string;
    instruction: string;
    accessToken: string;
    platform?: RewritePlatform;
  }) => Promise<string>;
  /** Persists the caption through the per-platform save path. */
  saveCaption: (text: string) => Promise<boolean>;
  toast: {
    success: (msg: string, opts?: { action?: { label: string; onClick: () => void } }) => void;
    error: (msg: string) => void;
  };
}

/**
 * Runs the caption rewrite control flow:
 *  1. Resolves the original caption (Axis 1).
 *  2. Guards on empty original.
 *  3. Calls the injected `rewriteCaption` service.
 *  4. Saves via `saveCaption` (Axis 3 write).
 *  5. Shows a success toast whose Undo action calls `saveCaption(original)` (Axis 3 undo).
 */
export async function runCaptionEdit(deps: RunCaptionEditDeps): Promise<void> {
  const {
    activeCaptionSource,
    captionDraft,
    activeCaptionIsDirty,
    activePlatform,
    instruction,
    accessToken,
    rewriteCaption,
    saveCaption,
    toast,
  } = deps;

  // Axis 1: use dirty draft if the user has unsaved edits, otherwise the
  // resolved per-tab source (master on primary tab, platform_captions[tab]
  // on secondary tabs). Never fall through to post.content.
  const originalCaption = (activeCaptionIsDirty ? captionDraft : activeCaptionSource) ?? '';

  if (!originalCaption.trim()) {
    toast.error('Add a caption to rewrite first');
    return;
  }

  if (!accessToken) {
    toast.error('Sign in required');
    return;
  }

  try {
    const rewritten = await rewriteCaption({
      original: originalCaption,
      instruction,
      accessToken,
      platform: toRewritePlatform(activePlatform),
    });

    // Axis 3 write
    const saved = await saveCaption(rewritten);
    if (!saved) throw new Error('Failed to save rewrite');

    // Axis 3 Undo — restores ORIGINAL through the same per-platform save path
    toast.success('Caption rewritten', {
      action: {
        label: 'Undo',
        onClick: () => {
          void (async () => {
            const undone = await saveCaption(originalCaption);
            if (undone) toast.success('Rewrite undone');
            else toast.error('Undo failed');
          })();
        },
      },
    });
  } catch (err) {
    toast.error(friendlyMessageWithRetry(err));
  }
}
